
import { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface FavoriteButtonProps {
  airportId: number;
  initialFavorited?: boolean;
  className?: string;
  onToggle?: (newState: boolean) => void;
}

export function FavoriteButton({ 
  airportId, 
  initialFavorited = false, 
  className = "",
  onToggle 
}: FavoriteButtonProps) {
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Update local state when initialFavorited prop changes
  useEffect(() => {
    setIsFavorited(initialFavorited);
  }, [initialFavorited]);

  const toggleFavorite = async () => {
    try {
      setIsLoading(true);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        toast({
          variant: "destructive",
          description: "You must be logged in to favorite airports",
        });
        return;
      }
      
      if (isFavorited) {
        // Remove favorite
        const { error } = await supabase
          .from('airport_favorites')
          .delete()
          .eq('airport_id', airportId)
          .eq('user_id', user.id);

        if (error) throw error;
        
        toast({
          description: "Airport removed from favorites",
        });
      } else {
        // Add favorite
        const { error } = await supabase
          .from('airport_favorites')
          .insert({
            airport_id: airportId,
            user_id: user.id
          });

        if (error) throw error;

        toast({
          description: "Airport added to favorites",
        });
      }

      const newState = !isFavorited;
      setIsFavorited(newState);
      
      // Notify parent component of the state change
      if (onToggle) {
        onToggle(newState);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        variant: "destructive",
        description: "Failed to update favorite status",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={isMobile ? "ghost" : "outline"}
      size={isMobile ? "sm" : "icon"}
      disabled={isLoading}
      onClick={toggleFavorite}
      className={`${
        isMobile 
          ? "group transition-all duration-300 hover:bg-pink-50 dark:hover:bg-pink-950/50" 
          : ""
      } ${className}`}
      aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
    >
      <Heart 
        className={`${
          isMobile 
            ? `h-5 w-5 transition-all duration-300 ${
                isFavorited 
                  ? "fill-red-500 text-red-500 scale-110" 
                  : "text-muted-foreground group-hover:text-red-500 group-hover:scale-110"
              }`
            : `h-4 w-4 ${
                isFavorited ? "fill-current text-red-500" : ""
              }`
        }`}
      />
    </Button>
  );
}
